import axios, { AxiosResponse, AxiosError } from 'axios';
import { eventBus } from '@/utils/eventBus';
import { reportCustomLog } from '@/config/apm';
import { redirectToLogin } from '@/utils/auth';
import { getCurrentLanguage } from '@/i18n/config';
import { classifyFailReason } from '@/observability/failClass';
import {
  classifyRequestError,
  extractTraceId,
  getStoredTraceId,
  isHtmlErrorPage,
  reportRequestAbnormal,
  recordRequestStart,
  recordRequestSettled,
} from '@/observability/apiError';
// 标准响应格式接口
export interface ApiResponse<T = any> {
  code: number;
  data: T;
  message: string;
  success?: boolean; // 根据您的API，这个字段可能不存在
}

// 自定义API错误类
export class ApiError extends Error {
  public code: number;
  public data?: any;

  constructor(message: string, code: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.data = data;
  }
}

// messageApi removed - using console.error for error logging
// 在开发环境中，使用相对路径以利用 Vite 代理
// 在生产环境中，使用完整的 URL
// const isDev = import.meta.env.DEV;
// const baseURL = isDev
//   ? import.meta.env.VITE_APP_BASE_API || '' // 开发环境：使用相对路径，走 Vite 代理
//   : `${import.meta.env.VITE_APP_BASE_URL || ''}${import.meta.env.VITE_APP_BASE_API || ''}`; // 生产环境：使用完整 URL
// console.log('baseURL', baseURL, 'isDev', isDev);

// prod走实际，其他走本地代理
const isProd = import.meta.env.MODE === 'prod';
console.log('isProd', isProd);
console.log('process.env.NODE_ENV', process.env.NODE_ENV);
const baseURL = isProd ? `${import.meta.env.VITE_APP_BASE_URL || ''}${import.meta.env.VITE_APP_BASE_API || ''}` : import.meta.env.VITE_APP_BASE_API || '';

const request = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// 获取当前语种：统一走 i18n/config（用户手动选择 > 品牌站点默认语种 > 全局默认），
// 保证 Accept-Language 与 UI 实际展示语种一致（如 juxflow 默认英文）

// 请求拦截器
request.interceptors.request.use(
  config => {
    config.headers = config.headers || {};
    const token = localStorage.getItem('anmo_ai_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // 注入当前语种参数
    config.headers['Accept-Language'] = getCurrentLanguage();
    // 如果是 FormData，删除 Content-Type，让浏览器自动设置
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    // X2 全站故障检测：统计请求总数（失败率的分母）。
    // 放在 return 前，确保只统计真正发出的请求；埋点失败绝不影响业务。
    try {
      recordRequestStart();
    } catch {
      /* 忽略 */
    }
    return config;
  },
  error => {
    // X2：配置阶段就失败（请求尚未真正发出）也要计入分母，
    // 否则后续错误拦截器记的 failed 会大于 total，失败率失真（>100%）。
    // 失败由错误拦截器统一记录，此处不重复计 failed。
    try {
      recordRequestStart();
    } catch {
      /* 忽略 */
    }
    return Promise.reject(error);
  }
);

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // 如果请求是为了获取文件流 (blob)，则直接返回响应，跳过JSON处理逻辑
    // 这是为了兼容文件下载等非JSON响应的场景
    if (response.config.responseType === 'blob') {
      // X2：文件流视为成功
      try {
        recordRequestSettled(true);
        // 提取 x-traceid 缓存到 config，错误拦截器按 config 引用读取
        extractTraceId(response.headers, response.config);
      } catch {
        /* 忽略 */
      }
      return response;
    }

    // 直接返回AxiosResponse，让调用方处理 response.data
    // 校验逻辑可以放在这里，如果业务失败则抛出错误，让调用方catch
    const apiResponse = response.data;
    // 兼容后端返回 msg 字段（非标准 message 字段）
    apiResponse.message = apiResponse.message ?? apiResponse.msg;
    const isSuccess = apiResponse.code === 0; // 只有code为0才是成功
    if (!isSuccess) {
      // 根据错误码进行不同的处理
      switch (apiResponse.code) {
        // 参数错误
        case 400000:
          console.error(apiResponse.message || '调用参数错误，不明确具体字段');
          break;
        case 400001:
          console.error(apiResponse.message || 'Token无效');
          localStorage.removeItem('anmo_ai_token');
          localStorage.removeItem('user_token');
          localStorage.removeItem('anmo_ai_user_info');
          // 可以在这里触发重新登录逻辑
          redirectToLogin();
          break;
        case 400002:
          console.error(apiResponse.message || '认证失败');
          localStorage.removeItem('anmo_ai_token');
          localStorage.removeItem('user_token');
          localStorage.removeItem('anmo_ai_user_info');
          // 可以在这里触发重新登录逻辑
          redirectToLogin();
          break;
        case 400004:
          console.error(apiResponse.message || 'Ticket 不存在');
          break;
        case 400010:
          apiResponse.message = '账号不存在';
          console.error(apiResponse.message);
          break;
        case 400011:
          apiResponse.message = '密码错误';
          console.error(apiResponse.message);
          break;
        case 400012:
          apiResponse.message = '账号已禁用';
          console.error(apiResponse.message);
          break;

        // 内部错误
        case 500000:
          console.error(apiResponse.message || '通用内部错误');
          break;
        case 500001:
          console.error(apiResponse.message || 'Redis 内部错误');
          break;
        case 500002:
          console.error(apiResponse.message || 'Mysql 内部错误');
          break;
        case 500003:
          console.error(apiResponse.message || '主站服务内部错误');
          break;
        case 500004:
          console.error(apiResponse.message || 'AES 加解密失败');
          break;
        case 600011:
          // message.warning('正常充值或购买会员后启用此模型')
          console.log('[Request] 触发 toastRecharge 事件，当前监听器数量:', eventBus.getListenerCount('toastRecharge'));
          eventBus.emit('toastRecharge');
          break;
        // 兼容旧的401处理
        case 401:
          console.error(apiResponse.message || '认证失败');
          localStorage.removeItem('anmo_ai_token');
          localStorage.removeItem('user_token');
          localStorage.removeItem('userInfo');
          console.warn('Token expired or invalid, redirecting to login is suggested.');
          // 可以在这里触发重新登录逻辑
          redirectToLogin();
          break;

        // 默认错误处理
        default:
          console.error(apiResponse.message || '请求处理失败');
          break;
      }

      // 在 switch/case 之后创建 apiError，确保使用覆盖后的 message
      const apiError = new ApiError(apiResponse.message, apiResponse.code, apiResponse.data);

      // 上报业务错误码到 APM——SDK 的 ajax/fetch 插件看这次请求 HTTP 200，判定为「成功」，
      // 采不到这类 code!==0 的业务失败，故此处手动补。
      // ⚠️ 2026-09-01 修正维度爆炸：原先把 url+method+request+response 拼成一个长字符串塞进
      // extra.requestData——SDK 文档明载字符串类 extra 会被转成日志 category，几乎每条都不同的
      // 长字符串当 category 用，等于每条自成一类，筛选/分组形同虚设，还可能超 1024 字符被截断。
      // 改为：低基数字段（errorCode/errorClass）留在 extra 供筛选；高基数详情改走 content。
      reportCustomLog(
        JSON.stringify({
          message: apiResponse.message || '请求处理失败',
          url: response.config?.url,
          method: response.config?.method,
          request: response.config?.data,
          response: response.data,
        }).slice(0, 1024),
        'error',
        {
          errorCode: apiResponse.code.toString(),
          errorClass: classifyFailReason({ code: apiResponse.code }),
          page: window.location.pathname,
        }
      );

      // 抛出自定义错误，包含错误码信息
      throw apiError;
    }
    // 对于业务成功的情况，我们依然返回整个AxiosResponse对象
    // 这样，封装的get/post等方法可以从中提取.data作为ApiResponse
    // X2：业务成功（code === 0）计入成功；缺了它失败率会恒为 100%
    try {
      recordRequestSettled(true);
      // 提取 x-traceid 缓存到 config（成功响应也带，便于业务错误码日志取用）
      extractTraceId(response.headers, response.config);
    } catch {
      /* 忽略 */
    }
    return response;
  },
  (error: AxiosError) => {
    // ── 埋点：接口层异常观测（X1）+ 全站故障统计（X2）──────────────────────
    // ⚠️ 边界（2026-09-07 review 修复）：响应拦截器对业务错误码（HTTP 200 但
    // code!==0）会 throw 自定义 ApiError，同样落到这里。这类**不是网络层故障**：
    //   1. 业务日志已在响应拦截器单独上报（reportCustomLog），此处再发
    //      api_request_abnormal 会造成**双重上报**；
    //   2. 业务失败 ≠ 接口故障，计入 X2 会把参数校验类错误误判为「全站故障」。
    // 故用 axios.isAxiosError 区分：仅真正的 Axios 错误（网络/HTTP 层）才进入 X1/X2。
    // 纯前端、全程 try/catch：任何埋点异常都不影响后续正常错误处理流程。
    try {
      if (axios.isAxiosError(error)) {
        recordRequestSettled(false);
        // 提取 x-traceid：优先从错误响应头拿，缓存到 config（供后续日志使用）
        if (error.response) {
          extractTraceId(error.response.headers, error.config);
        }
        // 从 cache 读出（同一请求的 config 引用相同，即使成功响应已设过也能读到）
        const traceId = getStoredTraceId(error.config);
        const errClass = classifyRequestError(error);
        // 有响应但返回的是 HTML 错误页 → 被网关/nginx 拦截（前端可精确识别，无需其他端配合）
        const blockedByGateway =
          !!error.response && isHtmlErrorPage(error.response as { headers?: Record<string, unknown>; data?: unknown });
        reportRequestAbnormal({
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          errorClass: blockedByGateway ? 'nginx_blocked' : errClass,
          // 开启 CORS 精确探测：失败时异步发 no-cors HEAD 区分 CORS vs 网络不可达
          // 代价：失败时多 1 个请求（按 host 60s 冷却，不会形成风暴）
          withProbe: true,
          // 后端给每条请求分配的唯一 ID，排查时直接拿去找后端日志
          traceId,
          detail: { message: error.message },
        });
      }
    } catch {
      /* 埋点失败绝不影响业务流程 */
    }

    let errorMessage = '请求发生错误';
    if (error.response) {
      // 服务器响应了错误状态码
      // 尝试从 error.response.data 获取后端返回的错误信息
      const apiError = error.response.data as Partial<ApiResponse>;
      errorMessage =
        apiError?.message || error.response.statusText || `服务器错误 (${error.response.status})`;
      // ⚠️ 2026-09-01 去重删除：此前这里手动 reportCustomLog 上报 HTTP 状态码错误，
      // 但 SDK 的 ajax/fetch 插件（src/config/apm.ts）本就会自动采集所有非 2xx 响应的
      // status/duration/url，重复上报只会多占配额、不增加信息量。HTTP 层错误直接看
      // APM「接口分析」看板即可，不需要在这里重复一遍。
      if (error.response.status === 401) {
        localStorage.removeItem('anmo_ai_token');
          localStorage.removeItem('user_token');
        console.warn('Unauthorized access (401), redirecting to login is suggested.');
        // 可以在这里触发重新登录逻辑
        // window.location.href = '/home';
        redirectToLogin();
      }
    } else if (error.request) {
      errorMessage = '网络连接错误，请检查您的网络设置';
    } else {
      // 对于其他类型的错误，使用统一的错误处理
      if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        errorMessage = '网络连接失败，请检查您的网络设置';
      } else if (error.message?.includes('timeout')) {
        errorMessage = '请求超时，请稍后重试';
      } else {
        errorMessage = '请求发生错误，请稍后重试';
      }
    }
    console.error(errorMessage);
    return Promise.reject(error);
  }
);

// 封装的HTTP方法，从 response.data 中提取 ApiResponse
export const get = async <T = any>(url: string, params?: any): Promise<ApiResponse<T>> => {
  const response = await request.get<ApiResponse<T>>(url, { params });
  return response.data;
};

export const post = async <T = any>(
  url: string,
  data?: any,
  config?: { timeout?: number }
): Promise<ApiResponse<T>> => {
  const response = await request.post<ApiResponse<T>>(url, data, config);
  return response.data;
};

export const put = async <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
  const response = await request.put<ApiResponse<T>>(url, data);
  return response.data;
};

export const del = async <T = any>(url: string, params?: any): Promise<ApiResponse<T>> => {
  const response = await request.delete<ApiResponse<T>>(url, { params });
  return response.data;
};

/**
 * 封装的POST方法，用于请求文件流并触发下载
 * @param url 请求的URL
 * @param data POST请求体
 * @param fileName 下载时使用的文件名
 */
export const postAndDownloadFile = async (
  url: string,
  data?: any,
  fileName?: string
): Promise<void> => {
  try {
    const response = await request.post(url, data, {
      responseType: 'blob', // 关键：期望响应类型为二进制数据
      timeout: 180000, // 3分钟超时
      // 我们需要一个方法来跳过默认的 onFulfilled 响应拦截器
      // 因为它会尝试将 blob 解析为 JSON，这会引发错误。
      // 一个简单的技巧是添加一个自定义的 transformResponse
      transformResponse: [
        data => {
          // 不要对响应做任何转换
          return data;
        },
      ],
    });

    // 'response' 在这里是 AxiosResponse<Blob>
    // 后端可能以 HTTP 200 + JSON 返回业务错误（非文件流）：此时按 code/message 抛错，交由调用方统一提示。
    const contentType = (response.headers['content-type'] || '') as string;
    if (contentType.includes('application/json')) {
      const text = await (response.data as Blob).text();
      const json = JSON.parse(text) as ApiResponse;
      // 下载失败以 JSON 返回：抛出携带 code 的 ApiError，由调用方按 code 提示（如 600034=无可下载内容）。
      throw new ApiError(json.message || '下载失败', json.code, json.data);
    }

    const blob = new Blob([response.data], { type: response.headers['content-type'] });

    // 从 Content-Disposition 头获取文件名
    let downloadFileName = fileName;
    const disposition = response.headers['content-disposition'];
    if (disposition) {
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?([^;]+)/);
      if (filenameMatch && filenameMatch[1]) {
        downloadFileName = decodeURIComponent(filenameMatch[1].replace(/"/g, ''));
      }
    }

    // 如果没有从 header 获取到文件名，并且没有提供 fileName 参数，则使用一个默认值
    if (!downloadFileName) {
      console.warn('无法从响应头确定文件名，将使用默认文件名 "download"');
      downloadFileName = 'download';
    }

    // 创建一个链接并触发下载
    const link = document.createElement('a');
    const urlBlob = window.URL.createObjectURL(blob);
    link.href = urlBlob;
    link.setAttribute('download', downloadFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(urlBlob); // 释放内存
  } catch (error) {
    console.error('下载文件时出错:', error);
    // 已是业务错误（含后端 code/message）：直接上抛，由调用方按 code 统一提示。
    if (error instanceof ApiError) throw error;
    // HTTP 非 2xx 且响应体为 blob：尝试解析出后端 code/message，包装为 ApiError 上抛。
    if (error instanceof AxiosError && error.response && error.response.data) {
      const errorBlob = error.response.data as Blob;
      const errorText = await errorBlob.text();
      try {
        const errorJson = JSON.parse(errorText) as ApiResponse;
        throw new ApiError(errorJson.message || '下载失败', errorJson.code, errorJson.data);
      } catch (parseError) {
        if (parseError instanceof ApiError) throw parseError;
        throw new ApiError('下载失败，且无法解析服务器返回的错误信息。', -1);
      }
    }
    // 重新抛出错误，以便调用堆栈中的上层可以捕获它
    throw error;
  }
};

/**
 * @deprecated The cancelAllRequests functionality and its underlying pendingRequests logic were removed for simplification.
 * This is a stub function to prevent import errors. It does not cancel any requests.
 */
export const cancelAllRequests = () => {
  console.warn(
    'cancelAllRequests was called, but its functionality has been removed. No requests were cancelled.'
  );
  // Original logic depended on pendingRequests Map which has been removed.
};

export const initRequestMessage = (messageInstance: { error: (content: string) => void }) => {
  if (messageInstance) {
    // setMessageApi removed - using console.error for error logging
    console.log('Request message API initialized.');
  }
};

export default request;
